export type ApiCoverageStatus = "tested" | "needs_review" | "missing";
export type ApiCoverageStrength = "explicit" | "domain";

export interface BackendApiCoverageSummary {
  total_endpoints: number;
  tested_endpoints: number;
  review_endpoints: number;
  missing_endpoints: number;
  total_tests: number;
  linked_tests: number;
  frontend_api_calls: number;
  admin_frontend_exposed_endpoints: number;
  admin_frontend_unexposed_endpoints: number;
  methods: Record<string, number>;
  scopes: Record<string, number>;
}

export interface BackendApiEndpointTestLink {
  test_id: string;
  strength: ApiCoverageStrength;
}

export interface BackendApiEndpoint {
  id: string;
  slug: string;
  title: string;
  title_source: "api_doc" | "code";
  method: string;
  path: string;
  module: string;
  category: string;
  route_file: string;
  line: number;
  scope: string;
  status: ApiCoverageStatus;
  tests: BackendApiEndpointTestLink[];
  doc: BackendApiDoc;
  code: BackendApiCodeFacts;
  frontend_exposure: BackendApiFrontendExposure;
}

export interface BackendApiFrontendExposure {
  exposed: boolean;
  calls: BackendApiFrontendCall[];
}

export interface BackendApiFrontendCall {
  file: string;
  method: string;
  path: string;
}

export interface BackendApiCodeFacts {
  auth: string;
  middleware: string[];
  path_params: string[];
  query_params: string[];
  headers: string[];
  body: string[];
  validators: string[];
  response_statuses: string[];
  response: string[];
  response_json: string[];
  error_codes: string[];
  env_bindings: string[];
  service_calls: string[];
  logic_steps: string[];
  validation_checks: string[];
}

export interface BackendApiDoc {
  title: string;
  section: string;
  subsection: string;
  auth: string;
  status_codes: string;
  request_fields: BackendApiRequestField[];
  request_summary: string;
  response_summary: string;
  error_codes: string[];
  documented: boolean;
}

export interface BackendApiRequestField {
  name: string;
  type: string;
  required: string;
  description: string;
  columns: string[];
}

export interface BackendApiTestCase {
  id: string;
  title: string;
  logic: string;
  method: string;
  source_path: string;
  linked_endpoint_ids: string[];
}

export interface BackendApiCoverageSnapshot {
  schema_version: 1;
  generated_at: string;
  summary: BackendApiCoverageSummary;
  endpoints: BackendApiEndpoint[];
  tests: BackendApiTestCase[];
}

export interface BackendApiCoverageFilters {
  query: string;
  module: string;
  method: string;
  scope: string;
  status: string;
}

export function parseBackendApiCoverageSnapshot(
  value: unknown,
): BackendApiCoverageSnapshot | null {
  if (!isRecord(value) || value.schema_version !== 1) return null;
  if (!isRecord(value.summary)) return null;
  if (!Array.isArray(value.endpoints) || !Array.isArray(value.tests)) {
    return null;
  }
  const endpoints = value.endpoints.map(parseEndpoint);
  const tests = value.tests.filter(isTestCase);
  if (endpoints.some((endpoint) => endpoint === null)) return null;
  if (tests.length !== value.tests.length) return null;
  return {
    schema_version: 1,
    generated_at: String(value.generated_at),
    summary: parseSummary(value.summary),
    endpoints: endpoints.filter((endpoint) => endpoint !== null),
    tests,
  };
}

function parseSummary(
  value: Record<string, unknown>,
): BackendApiCoverageSummary {
  return {
    total_endpoints: readNumber(value.total_endpoints),
    tested_endpoints: readNumber(value.tested_endpoints),
    review_endpoints: readNumber(value.review_endpoints),
    missing_endpoints: readNumber(value.missing_endpoints),
    total_tests: readNumber(value.total_tests),
    linked_tests: readNumber(value.linked_tests),
    frontend_api_calls: readNumber(value.frontend_api_calls),
    admin_frontend_exposed_endpoints: readNumber(
      value.admin_frontend_exposed_endpoints,
    ),
    admin_frontend_unexposed_endpoints: readNumber(
      value.admin_frontend_unexposed_endpoints,
    ),
    methods: readNumberRecord(value.methods),
    scopes: readNumberRecord(value.scopes),
  };
}

function parseEndpoint(value: unknown): BackendApiEndpoint | null {
  if (!isEndpointBase(value)) return null;
  return {
    id: value.id,
    slug: value.slug,
    title: value.title,
    title_source: value.title_source,
    method: value.method,
    path: value.path,
    module: value.module,
    category: value.category,
    route_file: value.route_file,
    line: value.line,
    scope: value.scope,
    status: value.status,
    tests: value.tests,
    doc: value.doc,
    code: value.code,
    frontend_exposure: parseFrontendExposure(value.frontend_exposure),
  };
}

function isEndpointBase(value: unknown): value is Omit<
  BackendApiEndpoint,
  "frontend_exposure"
> & {
  frontend_exposure?: unknown;
} {
  if (!isRecord(value) || !Array.isArray(value.tests)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.slug === "string" &&
    typeof value.title === "string" &&
    isTitleSource(value.title_source) &&
    typeof value.method === "string" &&
    typeof value.path === "string" &&
    typeof value.module === "string" &&
    typeof value.category === "string" &&
    typeof value.route_file === "string" &&
    typeof value.line === "number" &&
    typeof value.scope === "string" &&
    isStatus(value.status) &&
    value.tests.every(isEndpointTestLink) &&
    isDoc(value.doc) &&
    isCodeFacts(value.code)
  );
}

function parseFrontendExposure(value: unknown): BackendApiFrontendExposure {
  if (!isRecord(value)) return { exposed: false, calls: [] };
  const calls = Array.isArray(value.calls)
    ? value.calls.filter(isFrontendCall)
    : [];
  return {
    exposed: value.exposed === true,
    calls,
  };
}

function isFrontendCall(value: unknown): value is BackendApiFrontendCall {
  return (
    isRecord(value) &&
    typeof value.file === "string" &&
    typeof value.method === "string" &&
    typeof value.path === "string"
  );
}

function isCodeFacts(value: unknown): value is BackendApiCodeFacts {
  return (
    isRecord(value) &&
    typeof value.auth === "string" &&
    isStringArray(value.middleware) &&
    isStringArray(value.path_params) &&
    isStringArray(value.query_params) &&
    isStringArray(value.headers) &&
    isStringArray(value.body) &&
    isStringArray(value.validators) &&
    isStringArray(value.response_statuses) &&
    isStringArray(value.response) &&
    isStringArray(value.response_json) &&
    isStringArray(value.error_codes) &&
    isStringArray(value.env_bindings) &&
    isStringArray(value.service_calls) &&
    isStringArray(value.logic_steps) &&
    isStringArray(value.validation_checks)
  );
}

function isDoc(value: unknown): value is BackendApiDoc {
  return (
    isRecord(value) &&
    typeof value.title === "string" &&
    typeof value.section === "string" &&
    typeof value.subsection === "string" &&
    typeof value.auth === "string" &&
    typeof value.status_codes === "string" &&
    typeof value.request_summary === "string" &&
    typeof value.response_summary === "string" &&
    typeof value.documented === "boolean" &&
    Array.isArray(value.request_fields) &&
    value.request_fields.every(isRequestField) &&
    Array.isArray(value.error_codes) &&
    value.error_codes.every((item) => typeof item === "string")
  );
}

function isRequestField(value: unknown): value is BackendApiRequestField {
  return (
    isRecord(value) &&
    typeof value.name === "string" &&
    typeof value.type === "string" &&
    typeof value.required === "string" &&
    typeof value.description === "string" &&
    Array.isArray(value.columns) &&
    value.columns.every((item) => typeof item === "string")
  );
}

function isEndpointTestLink(
  value: unknown,
): value is BackendApiEndpointTestLink {
  return (
    isRecord(value) &&
    typeof value.test_id === "string" &&
    isStrength(value.strength)
  );
}

function isTestCase(value: unknown): value is BackendApiTestCase {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string" &&
    typeof value.logic === "string" &&
    typeof value.method === "string" &&
    typeof value.source_path === "string" &&
    Array.isArray(value.linked_endpoint_ids) &&
    value.linked_endpoint_ids.every((item) => typeof item === "string")
  );
}

function isStatus(value: unknown): value is ApiCoverageStatus {
  return value === "tested" || value === "needs_review" || value === "missing";
}

function isStrength(value: unknown): value is ApiCoverageStrength {
  return value === "explicit" || value === "domain";
}

function isTitleSource(value: unknown): value is "api_doc" | "code" {
  return value === "api_doc" || value === "code";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function readNumber(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

function readNumberRecord(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, number] => {
      return typeof entry[1] === "number";
    }),
  );
}
